/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
import { Button } from '@casavo/base-ui'
import { kebabCase } from 'lodash'
import { map, pipe, sortBy } from 'lodash/fp'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'

import Breadcrumbs from '@/components/Breadcrumbs'
import { linkBuilder } from '@/components/Link/linkBuilder'
import { CateCity, useCities } from '@/context/Cities'
import { useCityPage } from '@/context/CityPageContext'
import Close from '@/design-lib/components/Icons/Close'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { marginLeft } from '@/shared-styles/styles.css'
import { Zone } from '@/types/cities'
import provinceUtils from '@/utils/cities/province-utils'
import { buildRelativeLocalizedLink } from '@/utils/navigation'

import { breadcrumbLink, breadcrumbModalHeader, breadcrumbModalInner, breadcrumbModalOuter } from './styles.css'

type Item = {
  cityValue: string
  kind: 'area' | 'city' | 'zone'
  label: string
  slug: string
}

export const LIST_ITEM_TEST_ID = 'breadcrumb-modal-list-item'
export const BreadcrumbModal: React.FC = () => {
  const { lang, t } = useTranslation()
  const cities = useCities()
  const router = useRouter()
  const currentCity = cities.router.city || cities.router.area
  const areaItem: Item = {
    cityValue: cities.router.area.name,
    kind: 'area',
    label: `${t('common:province-of', { area: cities.router.area.translatedCityName })}`,
    slug: cities.router.area.slug,
  }
  const mapCity = map(
    (city: CateCity): Item => ({
      cityValue: city.name,
      kind: 'city',
      label: city.translatedCityName,
      slug: city.slug,
    })
  )
  const mapZone = map(
    (zone: Zone): Item => ({
      cityValue: cities.router.city.slug,
      kind: 'zone',
      label: zone.label,
      slug: zone.slug,
    })
  )
  const sortActiveZone = (zones: Item[]) =>
    [...zones].sort((a, b) => {
      if (b.slug === cities.router?.zone?.slug) {
        return 1
      } else if (a.slug === cities.router?.zone?.slug) {
        return -1
      }
      return 0
    })
  const sortByLabel = sortBy<Item>('label')
  const makeZones = pipe(mapZone, sortByLabel, sortActiveZone)
  const makeCities = pipe(mapCity, sortByLabel)
  const { closeBreadcrumbModal } = useCityPage()

  /**
   * Creates items for the modal
   */
  const items = (() => {
    const toReturn: Item[][] = []

    if (provinceUtils.citySupportsProvince(cities.router.area.name, cities.list)) toReturn.push([areaItem])
    if (typeof cities.router.city !== 'undefined') {
      toReturn.push(makeCities([cities.router.city]))
      if (cities.router.city.city_zones.length > 0) toReturn.push(makeZones(cities.router.city.city_zones))
    } else {
      toReturn.push(makeCities(cities.list))
    }
    return toReturn
  })()

  /**
   * Retrieve correct href based on item type
   */
  function makeHref(item: Item) {
    switch (item.kind) {
      case 'area':
        return linkBuilder.listingByArea.url(lang, cities.list, item.slug)
      case 'city':
        return linkBuilder.listingByCity.url(lang, cities.list, item.slug)
      case 'zone':
        return linkBuilder.listingByZone.url(lang, cities.list, item.cityValue, item.slug)
    }
  }

  return (
    <div className={breadcrumbModalOuter}>
      <header className={breadcrumbModalHeader}>
        <Button
          ariaLabel="Close"
          className={marginLeft.auto}
          data-testid="close-breadcrumb-modal-button"
          Icon={(props) => <Close {...props} />}
          shapeVariant="round"
          styleVariant="tertiary"
          onClick={() => {
            closeBreadcrumbModal()
          }}
        />
      </header>

      <hr />
      <div
        className={sprinkles({
          paddingX: 'l',
          paddingY: 'm',
        })}
      >
        <Breadcrumbs cities={cities} />
      </div>
      <hr />

      <div className={breadcrumbModalInner}>
        {items.length > 0 && (
          <ul>
            {items.map((section, index) => (
              <React.Fragment key={`section-${index}`}>
                {section.map((item) => {
                  const href = makeHref(item)

                  const isSelected = buildRelativeLocalizedLink(router.asPath, lang) === href
                  const onElementClick = () =>
                    closeBreadcrumbModal('selectedItem', `${currentCity.name} - ${item.label}`)

                  return (
                    <li key={`${item.slug}-${kebabCase(item.label)}`} onClick={onElementClick}>
                      <Link passHref href={href}>
                        <a
                          className={isSelected ? breadcrumbLink.selected : breadcrumbLink.default}
                          data-testid={`${LIST_ITEM_TEST_ID}-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {item.label}
                        </a>
                      </Link>
                    </li>
                  )
                })}
                <hr />
              </React.Fragment>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
